// Password change (the signed-in user) and reset (an administrator, for another user).
import { useState } from "react";
import { api } from "../api/client";
import { useMutation } from "../api/resource";
import { w } from "../i18n";
import { Dialog } from "./overlay";
import { ErrorState, Notice } from "./ui";

const MIN = 12;

export function ChangePasswordDialog({ onClose }: { onClose: () => void }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [again, setAgain] = useState("");
  const [done, setDone] = useState(false);
  const change = useMutation(api.changePassword);
  const valid = current && next.length >= MIN && next === again;
  return <Dialog title={w("password.title")} onClose={onClose} size="sm"
    footer={done ? <button className="ws-btn primary" onClick={onClose}>{w("common.close")}</button>
      : <><button className="ws-btn" onClick={onClose}>{w("common.cancel")}</button>
        <button className="ws-btn primary" disabled={!valid || change.pending}
          onClick={async () => { if (await change.run(current, next)) setDone(true); }}>{w("password.save")}</button></>}>
    {done ? <Notice tone="positive">{w("password.done")}</Notice> : <>
      {([["pw-current", w("password.current"), current, setCurrent, "current-password"],
        ["pw-new", w("password.new"), next, setNext, "new-password"],
        ["pw-again", w("password.again"), again, setAgain, "new-password"]] as const).map(([id, text, value, set, auto]) =>
        <div key={id} className="ws-field"><label htmlFor={id}>{text}</label>
          <input id={id} className="ws-input" type="password" autoComplete={auto} value={value} onChange={e => set(e.target.value)} /></div>)}
      {next && again && next !== again && <p className="ws-field-error">{w("password.mismatch")}</p>}
      <p className="ws-small ws-muted" style={{ margin: 0 }}>{w("password.hint")}</p>
      {change.error && <ErrorState title={w("password.error")} error={change.error} />}
    </>}
  </Dialog>;
}

export function ResetPasswordDialog({ userId, name, onClose }: { userId: string; name: string; onClose: () => void }) {
  const [next, setNext] = useState("");
  const [done, setDone] = useState(false);
  const reset = useMutation(api.updateUser);
  return <Dialog title={w("password.resetTitle", { name })} onClose={onClose} size="sm"
    footer={done ? <button className="ws-btn primary" onClick={onClose}>{w("common.close")}</button>
      : <><button className="ws-btn" onClick={onClose}>{w("common.cancel")}</button>
        <button className="ws-btn primary" disabled={next.length < MIN || reset.pending}
          onClick={async () => { if (await reset.run(userId, { password: next })) setDone(true); }}>{w("password.reset")}</button></>}>
    {done ? <Notice tone="positive">{w("password.resetDone")}</Notice> : <>
      <div className="ws-field"><label htmlFor="pw-reset">{w("password.new")}</label>
        <input id="pw-reset" className="ws-input" type="password" autoComplete="new-password" value={next} onChange={e => setNext(e.target.value)} /></div>
      <p className="ws-small ws-muted" style={{ margin: 0 }}>{w("password.resetHint")}</p>
      {reset.error && <ErrorState title={w("password.error")} error={reset.error} />}
    </>}
  </Dialog>;
}
